import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Utensils, Plus, Info } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import MealForm from "../forms/MealForm";
import RecordActions from "../components/RecordActions";

const METHOD_LABEL = { per_100g: "Por 100 g", direct: "Direto", photo: "Foto", mixed: "Misto" };

export default function Meals() {
  const { t } = useI18n();
  const { activeProfileId, can } = useProfile();
  const [meals, setMeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const { data } = await api.get(`/profiles/${activeProfileId}/meals`);
    setMeals(data);
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("meals_title")}</h1>
          <p className="text-[#627D98] mt-1">{t("meals_sub")}</p>
        </div>
        {can("log_meals") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold shrink-0" data-testid="add-meal-btn">
                <Plus size={18} className="mr-1" /> {t("add_meal")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
              <DialogHeader><DialogTitle className="font-head text-xl">{t("add_meal")}</DialogTitle></DialogHeader>
              <MealForm onSaved={() => { setOpen(false); load(); }} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="df-card p-5">
        {meals.length === 0 ? (
          <p className="text-sm text-[#627D98]">{t("no_meals")}</p>
        ) : (
          <div className="divide-y divide-[#F0F5FA]">
            {meals.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-3" data-testid={`meal-row-${m.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-10 w-10 shrink-0 rounded-xl bg-[#E3EFF9] flex items-center justify-center"><Utensils size={18} className="text-[#1A73E8]" /></span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0D253F] truncate">{m.notes || t("reg_meal")}</div>
                    <div className="text-xs text-[#627D98] truncate">
                      {new Date(m.datetime).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      {" · "}{METHOD_LABEL[m.method] || m.method}{" · "}{m.author_name}
                    </div>
                  </div>
                </div>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-base font-bold whitespace-nowrap text-[#0D253F]">{m.total_carbs} {t("grams")}</span>
                  <RecordActions record={m} canEdit={can("correct_records")} onEdit={setEditRow} testid={`meal-${m.id}`} />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-[#627D98] flex items-center gap-1.5"><Info size={13} /> {t("photo_demo_note")}</p>

      {editRow && (
        <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
            <DialogHeader><DialogTitle className="font-head text-xl">{t("edit")} · {t("meals_title")}</DialogTitle></DialogHeader>
            <MealForm record={editRow} onSaved={() => { setEditRow(null); load(); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
